import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  me: () => api.get('/auth/me'),
  updateSettings: (data) => api.put('/auth/settings', data),
  verifyPassword: (password) => api.post('/auth/verify-password', { password }),
};

export const notesAPI = {
  list: () => api.get('/notes'),
  get: (id) => api.get(`/notes/${id}`),
  create: (data) => api.post('/notes', data),
  update: (id, data, sessionId) => api.put(`/notes/${id}${sessionId ? `?session_id=${sessionId}` : ''}`, data),
  delete: (id) => api.delete(`/notes/${id}`),
  permanentDelete: (id) => api.delete(`/notes/${id}/permanent`),
  emptyTrash: () => api.delete('/notes/trash/empty'),
  archive: (id) => api.post(`/notes/${id}/archive`),
  restore: (id) => api.post(`/notes/${id}/restore`),
  pin: (id) => api.post(`/notes/${id}/pin`),
  favorite: (id) => api.post(`/notes/${id}/favorite`),
  versions: (id) => api.get(`/notes/${id}/versions`),
  share: (id) => api.post(`/notes/${id}/share`),
  unshare: (id) => api.delete(`/notes/${id}/share`),
};

export const notebooksAPI = {
  list: () => api.get('/notebooks'),
  create: (data) => api.post('/notebooks', data),
  update: (id, data) => api.put(`/notebooks/${id}`, data),
  delete: (id) => api.delete(`/notebooks/${id}`),
};

export const tagsAPI = {
  list: () => api.get('/tags'),
  create: (data) => api.post('/tags', data),
  update: (id, data) => api.put(`/tags/${id}`, data),
  delete: (id) => api.delete(`/tags/${id}`),
};

export const templatesAPI = {
  list: () => api.get('/templates'),
  create: (data) => api.post('/templates', data),
  update: (id, data) => api.put(`/templates/${id}`, data),
  delete: (id) => api.delete(`/templates/${id}`),
};

export const remindersAPI = {
  list: () => api.get('/reminders'),
  create: (data) => api.post('/reminders', data),
  delete: (id) => api.delete(`/reminders/${id}`),
  complete: (id) => api.post(`/reminders/${id}/complete`),
};

export const searchAPI = {
  search: (query) => api.get('/search', { params: { q: query } }),
};

export const exportAPI = {
  exportNotes: () => api.get('/export/notes'),
  importNotes: (data) => api.post('/import/notes', data),
};

export const backupAPI = {
  exportBundle: (params) => api.post('/backup/export', null, { params }),
  restorePreview: (bundle) => api.post('/backup/restore/preview', bundle),
  restore: (bundle) => api.post('/backup/restore', bundle),
};

export const healthAPI = {
  get: () => api.get('/system/health'),
};

export const markdownImportAPI = {
  import: (data) => api.post('/import/markdown', data),
};

export const sharedAPI = {
  getSharedNote: (token) => api.get(`/shared/${token}`),
};

export const dailyAPI = {
  getOrCreate: (dateStr, notebookId) =>
    api.get(`/notes/daily/${dateStr}${notebookId ? `?notebook_id=${notebookId}` : ''}`),
};

export const inboxAPI = {
  ensure: () => api.post('/notebooks/ensure-inbox'),
};

export const journalAPI = {
  ensureNotebook: () => api.post('/notebooks/ensure-journal'),
  listEntries: () => api.get('/notes/journal'),
};

export const attachmentsAPI = {
  list: (noteId) => api.get(`/attachments/${noteId}`),
  upload: (noteId, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/attachments/upload?note_id=${noteId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress,
    });
  },
  // Chunked upload for large files
  initChunked: (noteId, filename, totalSize, totalChunks) =>
    api.post(`/attachments/upload/init?note_id=${noteId}&filename=${encodeURIComponent(filename)}&total_size=${totalSize}&total_chunks=${totalChunks}`),
  uploadChunk: (uploadId, chunkIndex, blob) => {
    const formData = new FormData();
    formData.append('file', blob, `chunk_${chunkIndex}`);
    return api.post(`/attachments/upload/${uploadId}/chunk?chunk_index=${chunkIndex}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  completeChunked: (uploadId) => api.post(`/attachments/upload/${uploadId}/complete`),
  delete: (attachmentId) => api.delete(`/attachments/file/${attachmentId}`),
  getFileUrl: (attachmentId) => {
    const token = localStorage.getItem('token');
    return `${API_URL}/attachments/file/${attachmentId}?auth=${token}`;
  },
};

export default api;
